import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GamificacaoResponse, PontuacaoGanhaResponse, BadgeConquistadaResponse } from 'app/shared/models/ei-models';
import { PontosToastComponent } from './pontos-toast/pontos-toast.component';
import { BadgeToastComponent } from './badge-toast/badge-toast.component';

/**
 * Serviço global para gerenciar notificações de gamificação
 * Usa toast notifications não invasivas ao invés de popups modais
 * 
 * NOTA: O backend já retorna pontuação unificada quando necessário
 * (ex: 120 pontos ao concluir última aula do módulo = 20 aula + 100 módulo)
 * Badges só são retornadas para usuários de plano 2+
 */
@Injectable({ providedIn: 'root' })
export class GamificacaoHandler {
  private readonly _snackBar = inject(MatSnackBar);
  
  // Fila de notificações para exibir em sequência
  private notificationQueue: Array<{ type: 'pontos' | 'badge', data: any }> = [];
  private isShowingNotification = false;
  
  // Duração padrão das notificações (10 segundos)
  private readonly NOTIFICATION_DURATION = 10000;
  
  /**
   * Processa uma resposta de gamificação e exibe as notificações apropriadas
   * @param response Resposta de gamificação do backend
   */
  processarGamificacao(response: GamificacaoResponse | null | undefined): void {
    if (!response || !response.houveConquista) {
      return;
    }
    
    // Adicionar pontos à fila
    if (response.pontuacao?.pontosGanhos) {
      this.notificationQueue.push({ type: 'pontos', data: response.pontuacao });
    }
    
    // Adicionar badges à fila (só vem preenchido para plano 2+)
    if (response.badgesConquistadas?.length > 0) {
      response.badgesConquistadas.forEach(badge => {
        this.notificationQueue.push({ type: 'badge', data: badge });
      });
    }
    
    // Iniciar processamento da fila
    this.processarFila();
  }
  
  /**
   * Exibe notificação toast de pontos ganhos
   */
  mostrarPontos(pontuacao: PontuacaoGanhaResponse): void {
    this.isShowingNotification = true;
    
    const snackBarRef = this._snackBar.openFromComponent(PontosToastComponent, {
      data: pontuacao,
      duration: this.NOTIFICATION_DURATION,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['gamificacao-toast', 'pontos-toast-container']
    });
    
    snackBarRef.afterDismissed().subscribe(() => {
      this.isShowingNotification = false;
      // Processar próxima notificação com delay
      setTimeout(() => this.processarFila(), 500);
    });
  }
  
  /**
   * Exibe notificação toast de badge conquistada
   */
  mostrarBadge(badge: BadgeConquistadaResponse): void {
    this.isShowingNotification = true;
    
    const snackBarRef = this._snackBar.openFromComponent(BadgeToastComponent, {
      data: badge,
      duration: this.NOTIFICATION_DURATION,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['gamificacao-toast', 'badge-toast-container']
    });
    
    snackBarRef.afterDismissed().subscribe(() => {
      this.isShowingNotification = false;
      // Processar próxima notificação com delay
      setTimeout(() => this.processarFila(), 500);
    });
  }
  
  /**
   * Processa a fila de notificações, uma por vez
   */
  private processarFila(): void {
    if (this.isShowingNotification || this.notificationQueue.length === 0) {
      return;
    }
    
    const notification = this.notificationQueue.shift();
    if (notification) {
      if (notification.type === 'pontos') {
        this.mostrarPontos(notification.data);
      } else if (notification.type === 'badge') {
        this.mostrarBadge(notification.data);
      }
    }
  }
  
  /**
   * Limpa a fila de notificações (útil em navegação)
   */
  limparFila(): void {
    this.notificationQueue = [];
  }
}
